import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { TooltipProvider } from '@/components/ui/tooltip';
import { ApiRequestError } from './lib/api';
import { adoptPreviewFromUrl } from './lib/customisation/preview';
import { SiteProvider } from './lib/customisation/site';
import { sessionQuery } from './lib/session';
import { ThemeProvider } from './lib/theme';
import { routeTree } from './routeTree.gen';
import './index.css';

// `?preview=draft` on any URL turns on customisation preview for this tab, before any
// query that depends on it runs.
adoptPreviewFromUrl();

const queryClient: QueryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (err) => {
      // A 401 from any query means the session ended (expired, revoked, suspended):
      // refresh session state so route guards send the user to the sign-in page.
      if (err instanceof ApiRequestError && err.status === 401) {
        void queryClient
          .invalidateQueries({ queryKey: sessionQuery.queryKey })
          .then(() => router.invalidate());
      }
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 10_000,
      // Don't retry 4xx: those won't fix themselves.
      retry: (count, err) =>
        !(err instanceof ApiRequestError && err.status >= 400 && err.status < 500) && count < 2,
    },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const root = document.getElementById('root');
if (!root) throw new Error('#root element missing from index.html');

createRoot(root).render(
  <StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <SiteProvider>
          <TooltipProvider delayDuration={300}>
            <RouterProvider router={router} />
          </TooltipProvider>
        </SiteProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
);
